import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, collection, onSnapshot, addDoc, doc, deleteDoc, query, where } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const Clients = () => {
  const currentUser = auth.currentUser;
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });

  // Load clients
  useEffect(() => {
    if (!currentUser) return;

    const firestore = getFirestore();
    const clientsRef = collection(firestore, 'users', currentUser.uid, 'clients');

    const unsubscribe = onSnapshot(clientsRef, snapshot => {
      const clientsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClients(clientsData);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Load invoices to calculate client stats
  useEffect(() => {
    if (!currentUser) return;

    const firestore = getFirestore();
    const invoicesRef = query(
      collection(firestore, 'invoices'),
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(invoicesRef, snapshot => {
      const invoicesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInvoices(invoicesData);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Calculate stats for each client based on their invoices
  const clientStats = useMemo(() => {
    const stats = {};
    invoices.forEach(inv => {
      const email = (inv.email || '').toLowerCase();
      if (!email) return;

      if (!stats[email]) {
        stats[email] = { totalBilled: 0, totalPaid: 0, invoiceCount: 0, lastInvoice: null };
      }

      const total = inv.total || inv.amount || inv.subtotal || 0;
      stats[email].invoiceCount += 1;
      stats[email].totalBilled += total;

      if (inv.status === 'PAID' || inv.status === 'paid') {
        stats[email].totalPaid += total;
      }

      const createdAt = inv.createdAt?.toDate?.() || new Date(inv.createdAt || 0);
      if (!stats[email].lastInvoice || createdAt > stats[email].lastInvoice) {
        stats[email].lastInvoice = createdAt;
      }
    });
    return stats;
  }, [invoices]);

  // Get client invoices for detail view
  const getClientInvoices = (clientEmail) => {
    return invoices
      .filter(inv => (inv.email || '').toLowerCase() === clientEmail.toLowerCase())
      .sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
  };

  // Filter and sort clients
  const filteredClients = useMemo(() => {
    let result = clients;

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q)
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      const statsA = clientStats[(a.email || '').toLowerCase()] || {};
      const statsB = clientStats[(b.email || '').toLowerCase()] || {};

      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'revenue':
          return (statsB.totalBilled || 0) - (statsA.totalBilled || 0);
        case 'invoices':
          return (statsB.invoiceCount || 0) - (statsA.invoiceCount || 0);
        case 'recent':
        default:
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
      }
    });

    return result;
  }, [clients, searchQuery, sortBy, clientStats]);

  const handleAddClient = async (e) => {
    e.preventDefault();

    try {
      const firestore = getFirestore();
      const clientsRef = collection(firestore, 'users', currentUser.uid, 'clients');

      await addDoc(clientsRef, {
        ...newClient,
        email: newClient.email.toLowerCase(),
        createdAt: new Date()
      });

      setNewClient({ name: '', email: '', company: '', phone: '' });
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to add client:', err);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to remove this client?')) return;

    try {
      const firestore = getFirestore();
      await deleteDoc(doc(firestore, 'users', currentUser.uid, 'clients', clientId));
      if (selectedClient?.id === clientId) {
        setSelectedClient(null);
      }
    } catch (err) {
      console.error('Failed to delete client:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const sortOptions = [
    { value: 'recent', label: 'Recently added' },
    { value: 'name', label: 'A → Z' },
    { value: 'name-desc', label: 'Z → A' },
    { value: 'revenue', label: 'Highest revenue' },
    { value: 'invoices', label: 'Most invoices' },
  ];

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-400 text-sm">Loading clients...</div>
      </div>
    );
  }

  const selectedStats = selectedClient
    ? clientStats[(selectedClient.email || '').toLowerCase()] || {}
    : {};
  const selectedInvoices = selectedClient
    ? getClientInvoices(selectedClient.email)
    : [];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 lg:px-6 pt-4 lg:pt-5 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
              <UserGroupIcon className="w-5 h-5 text-brand" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Clients</h1>
              <p className="text-xs text-gray-400">{clients.length} total</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <AnimatePresence mode="wait">
              {showSearch ? (
                <motion.div
                  key="search-input"
                  initial={{ width: 40, opacity: 0 }}
                  animate={{ width: 200, opacity: 1 }}
                  exit={{ width: 40, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="relative">
                    <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onBlur={() => !searchQuery && setShowSearch(false)}
                      placeholder="Search..."
                      className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 outline-none focus:border-brand/40"
                    />
                  </div>
                </motion.div>
              ) : (
                <motion.button
                  key="search-icon"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowSearch(true)}
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-brand hover:border-brand/30 transition-all"
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </motion.button>
              )}
            </AnimatePresence>

            {/* Add Client Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-brand text-white text-sm font-medium rounded-xl hover:bg-brand-hover transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Add Client</span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 lg:px-6 pb-4 min-h-0 flex gap-4">
        {/* Client List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`min-h-0 bg-gray-50/50 rounded-xl border border-gray-100 flex flex-col overflow-hidden ${
            selectedClient ? 'w-[360px] flex-shrink-0' : 'flex-1'
          }`}
        >
          {/* List Header */}
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
            <span className="text-xs text-gray-400">
              {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
              {searchQuery && ` matching "${searchQuery}"`}
            </span>

            {/* Sort dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all"
              >
                <span>{sortOptions.find(s => s.value === sortBy)?.label}</span>
                <ChevronDownIcon className={`w-3 h-3 transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {showSortMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowSortMenu(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -4, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.95 }}
                      className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-20 min-w-[140px]"
                    >
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortMenu(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-xs transition-all ${
                            sortBy === option.value
                              ? 'text-brand bg-brand/5'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Client List */}
          {filteredClients.length > 0 ? (
            <div className="flex-1 min-h-0 overflow-auto divide-y divide-gray-100/60">
              {filteredClients.map((client, i) => {
                const stats = clientStats[(client.email || '').toLowerCase()] || {};
                const isSelected = selectedClient?.id === client.id;

                return (
                  <motion.div
                    key={client.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.02 }}
                    onClick={() => setSelectedClient(isSelected ? null : client)}
                    className={`flex items-center justify-between px-4 py-3.5 hover:bg-white/60 transition-colors cursor-pointer group ${
                      isSelected ? 'bg-white' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-brand/10 flex items-center justify-center text-sm font-medium text-brand flex-shrink-0">
                        {(client.name || 'C')[0].toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {client.name || 'Unnamed'}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{client.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{formatCurrency(stats.totalBilled)}</p>
                        <p className="text-xs text-gray-400">{stats.invoiceCount || 0} invoices</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center py-12 px-6">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
                <UserGroupIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                {searchQuery ? 'No clients found' : 'No clients yet'}
              </p>
              <p className="text-xs text-gray-400 mb-4">
                {searchQuery ? 'Try a different search' : 'Add your first client'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-hover rounded-xl transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Client
                </button>
              )}
            </div>
          )}
        </motion.div>

        {/* Client Detail Panel */}
        <AnimatePresence>
          {selectedClient && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex-1 min-h-0 bg-white rounded-xl border border-gray-100 flex flex-col overflow-hidden"
            >
              {/* Detail Header */}
              <div className="px-6 py-5 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-brand/10 flex items-center justify-center text-xl font-medium text-brand">
                      {(selectedClient.name || 'C')[0].toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{selectedClient.name}</h2>
                      <p className="text-sm text-gray-500">{selectedClient.email}</p>
                      {selectedClient.company && (
                        <p className="text-xs text-gray-400 mt-0.5">{selectedClient.company}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDeleteClient(selectedClient.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setSelectedClient(null)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 mt-5">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Total Billed</p>
                    <p className="text-xl font-semibold text-gray-900">{formatCurrency(selectedStats.totalBilled)}</p>
                  </div>
                  <div className="w-px h-10 bg-gray-100" />
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Paid</p>
                    <p className="text-xl font-semibold text-emerald-600">{formatCurrency(selectedStats.totalPaid)}</p>
                  </div>
                  <div className="w-px h-10 bg-gray-100" />
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Invoices</p>
                    <p className="text-xl font-semibold text-gray-900">{selectedStats.invoiceCount || 0}</p>
                  </div>
                </div>
              </div>

              {/* Invoices List */}
              <div className="flex-1 min-h-0 overflow-auto">
                <div className="px-6 py-3 border-b border-gray-50 flex items-center justify-between sticky top-0 bg-white">
                  <p className="text-xs font-medium text-gray-500">Invoices</p>
                </div>

                {selectedInvoices.length > 0 ? (
                  <div className="divide-y divide-gray-50">
                    {selectedInvoices.map((invoice) => {
                      const total = invoice.total || invoice.amount || invoice.subtotal || 0;
                      const status = (invoice.status || 'PENDING').toUpperCase();
                      const date = invoice.createdAt?.toDate?.() || new Date();

                      return (
                        <div
                          key={invoice.id}
                          onClick={() => navigate(`/preview-invoice/${invoice.id}`)}
                          className="px-6 py-3 flex items-center justify-between hover:bg-gray-50/50 cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                              <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{formatCurrency(total)}</p>
                              <p className="text-xs text-gray-400">
                                {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            status === 'PAID'
                              ? 'bg-emerald-50 text-emerald-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {status === 'PAID' ? 'Paid' : 'Pending'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 px-6">
                    <DocumentTextIcon className="w-8 h-8 text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">No invoices yet</p>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => window.location.href = `mailto:${selectedClient.email}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                  >
                    <EnvelopeIcon className="w-4 h-4" />
                    Email
                  </button>
                  <button
                    onClick={() => navigate('/app/invoices/new')}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-hover rounded-xl transition-colors"
                  >
                    <DocumentTextIcon className="w-4 h-4" />
                    New Invoice
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Add Client Modal */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setShowAddForm(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Add Client</h2>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleAddClient} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Name *</label>
                  <input
                    type="text"
                    value={newClient.name}
                    onChange={(e) => setNewClient(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Client name"
                    className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 outline-none focus:border-brand/40 focus:bg-white transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Email *</label>
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="client@email.com"
                    className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 outline-none focus:border-brand/40 focus:bg-white transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Company</label>
                  <input
                    type="text"
                    value={newClient.company}
                    onChange={(e) => setNewClient(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Company name (optional)"
                    className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 outline-none focus:border-brand/40 focus:bg-white transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={newClient.phone}
                    onChange={(e) => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone number (optional)"
                    className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 outline-none focus:border-brand/40 focus:bg-white transition-colors"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-5 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-hover rounded-xl transition-colors"
                  >
                    Add Client
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Clients;
